/**
 * The hint text `handleAssistedGameMoveAnalysisSuccess` built (PlayChess
 * index.js L693-729), ported verbatim onto `getChessMoveNumber`'s V2 home.
 * Pure: the API response in, the card's `label` / `text` / `text2` out — so
 * `hooks/useAssistEffects.ts` stays a thin wire between the socket edge and
 * the REST call.
 *
 * fix L / L2: ALSO fills `mine` (the caller's own, from `assistStore.pending`
 * — never derived here), `san` / `moveNo` (so the card need not recompute
 * them) and `bestLine` (the server's best SAN line from the position BEFORE
 * this move). `label` stays a straight copy of the server's own
 * blunder/mistake/inaccuracy flags — this file never classifies a move
 * itself (`assistStore.ts`'s own header).
 */
import type {Hint, HintLabel} from './assistStore';
import {getChessMoveNumber} from './moveNumber';

/** `game/start_analyzing_move`'s response shape (the fields this reads). */
export type AssistedMoveResponse = {
  move_index: number;
  move: string;
  blunder?: boolean;
  mistake?: boolean;
  inaccuracy?: boolean;
  hint_response?: string;
  hint_response2?: string;
  /** fix L / L2: the server's best SAN line from the position BEFORE this move */
  best_possible_response?: string;
};

/**
 * fix Z1.1: a TOLERANT position match for `useAssistEffects.ts`'s own
 * `onSuccess` — the first TWO FEN fields (placement, side to move) are the
 * position; castling rights / en-passant / halfmove / fullmove are
 * normalisation noise, never a different position of the same game. Empty
 * on either side is never a match (nothing pending, or no fen on the wire).
 */
export const samePosition = (a: string, b: string): boolean => {
  if (!a || !b) {
    return false;
  }
  const head = (fen: string) => fen.split(' ').slice(0, 2).join(' ');
  return head(a) === head(b);
};

export const buildHint = (
  data: AssistedMoveResponse,
  isBlackTurnFirst: boolean,
  mine: boolean,
): Hint => {
  const moveNumber = getChessMoveNumber(data.move_index, isBlackTurnFirst);
  let label: HintLabel = '';
  let text = `${moveNumber} ${data.move} `;
  let text2 = `${moveNumber} ${data.move} `;

  if (data.blunder) {
    text += 'is a Blunder! ';
    text2 += 'is a Blunder! ';
    label = 'Blunder';
  } else if (data.mistake) {
    text += 'is a Mistake! ';
    text2 += 'is a Mistake! ';
    label = 'Mistake';
  } else if (data.inaccuracy) {
    text += 'is an Inaccuracy! ';
    text2 += 'is an Inaccuracy! ';
    label = 'Inaccuracy';
  }

  if (data.hint_response) {
    text += data.hint_response;
  }
  if (data.hint_response2) {
    text2 += data.hint_response2;
  }

  return {
    label,
    text,
    text2,
    mine,
    san: data.move,
    moveNo: moveNumber,
    bestLine: data.best_possible_response ?? '',
  };
};

export default buildHint;
